import {DTCampaignVersionType} from 'common/src/modules/campaigns/campaigns.types';
import createNavigateToCampaignWorkflowStep from 'common/src/modules/campaigns/utils/createNavigateToCampaignWorkflowStep';
import {CampaignStep} from 'common/src/modules/campaignWorkflow/campaignWorkflow.enums';
import {CONTROL_MODE} from 'common/src/modules/decisionTree/decisionTree.consts';
import {DTControlMode} from 'common/src/modules/decisionTree/treeTypes';
import {
  PollResult,
  ToolExecuteParams,
  ToolResult,
} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {poll} from 'common/src/modules/shared/copilot/utils/poll';
import {CLStore} from 'common/src/redux/store';
import _find from 'lodash/find';
import {z} from 'zod';
import {CampaignAnalyticsToolName} from '../campaignAnalyticsAgent.enums';
import campaignsApi from 'common/src/modules/campaigns/campaigns.api';

const inputSchema = z.object({
  campaignId: z.number().describe('The ID of the campaign to navigate to.'),
});

function navigateToCampaignInsightsTool(store: CLStore) {
  async function execute({
    input: {campaignId},
    toolUtils: {logger},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const {
      campaignPage: {insightsTab},
    } = state;

    if (insightsTab.measuredPeriod.timezone) return toolResult('Success');

    const campaignVersions = await campaignsApi.getCampaignVersionsList(campaignId);
    const versionNumber =
      _find(campaignVersions, {isActivated: true})?.number || DTCampaignVersionType.Latest;

    createNavigateToCampaignWorkflowStep(CampaignStep.Insights)({
      campaignId,
      versionNumber: versionNumber.toString(),
      queryParams: {
        [CONTROL_MODE]: DTControlMode.AnalyticsDashboard,
      },
    });

    function _isMounted(): PollResult {
      // Check that user is on campaign workflow page (never mind which tab)
      const state = store.getState();
      const {
        campaignPage: {insightsTab},
      } = state;

      if (insightsTab.measuredPeriod.timezone) {
        return {passed: true};
      }

      return {passed: false, reason: 'You are not on the campaign workflow page'};
    }

    await poll({
      checkFn: _isMounted,
      maxAttempts: 30,
      interval: 100,
      logger,
    });

    return toolResult('Success');
  }

  return tool({
    name: CampaignAnalyticsToolName.NavigateToCampaignInsights,
    displayName: 'Navigating to campaign insights',
    description: 'navigate to the campaign analytics page',
    execute,
    inputSchema: inputSchema,
  });
}

export default navigateToCampaignInsightsTool;
